import { Box, Flex, Text } from '@chakra-ui/react';
import { keyframes } from '@emotion/react';
import React from 'react';

// Pulse used on a node when its matching card is hovered. `currentColor` is set
// to the node's color so the expanding ring matches the item's color coding.
const nodePulse = keyframes`
  0%   { transform: scale(1);    box-shadow: 0 0 0 0 currentColor; }
  70%  { transform: scale(1.35); box-shadow: 0 0 0 5px transparent; }
  100% { transform: scale(1);    box-shadow: 0 0 0 0 transparent; }
`;

export interface TimelineItemData {
    key: string;
    /** Color of the node ring on the spine (defaults to a neutral gray). */
    nodeColor?: string;
    content: React.ReactNode;
}

export interface TimelineGroupData {
    key: string;
    /** Left-aligned date pill label, e.g. "Today" / "Apr 27". */
    label: string;
    /** Optional muted text shown at the right of the date row (e.g. a count). */
    rightLabel?: string;
    items: TimelineItemData[];
}

interface TimelineProps {
    groups: TimelineGroupData[];
}

// Geometry shared by the spine, nodes and connectors. All values are px.
const SPINE_X = 18;       // horizontal center of the vertical dashed spine
const NODE_SIZE = 11;     // diameter of the hollow node ring
const GUTTER = 46;        // width reserved on the left for spine + node + connector
// Vertical distance from the top of each row to the center of the node. Matches
// the vertical center of the card's leading icon (card top padding 12px + half
// of the 32px icon) so the node/connector always line up with the icon, even
// for taller cards that have subcontent.
const NODE_CENTER_Y = 28;

/**
 * Vertical timeline used by the activity log and order notification drawers.
 * Renders date pills with hollow node rings and dashed connectors leading out to
 * each item's card. The vertical line is drawn per-item and colored to match
 * that item's node, so the spine reads as a color-coded thread (e.g. the segment
 * by a green node is green). The line breaks cleanly under each date pill.
 */
const Timeline: React.FC<TimelineProps> = ({ groups }) => (
    <Box position="relative">
        <Box position="relative">
            {groups.map((group, groupIdx) => (
                <Box key={group.key} mt={groupIdx === 0 ? 0 : 5}>
                    {/* Date pill row */}
                    <Flex align="center" justify="space-between" mb={3} minH="22px">
                        <Box
                            bg="gray.100"
                            color="gray.600"
                            fontSize="xs"
                            fontWeight="semibold"
                            px={2.5}
                            py={1}
                            borderRadius="full"
                            position="relative"
                            zIndex={1}
                        >
                            {group.label}
                        </Box>
                        {group.rightLabel && (
                            <Text fontSize="xs" color="gray.400" fontWeight="medium">
                                {group.rightLabel}
                            </Text>
                        )}
                    </Flex>

                    {/* Items */}
                    <Box>
                        {group.items.map((item, itemIdx) => {
                            const isLast = itemIdx === group.items.length - 1;
                            const lineColor = item.nodeColor ?? 'gray.300';
                            return (
                                <Flex key={item.key} align="stretch" role="group">
                                    {/* Left gutter: colored line segment + node ring + connector */}
                                    <Box position="relative" w={`${GUTTER}px`} flexShrink={0}>
                                        {/* Vertical line segment, colored to match this item's node.
                                            Spans the full row (bridging the gap to the next item); the
                                            last item in a group stops at its node so the thread ends cleanly. */}
                                        <Box
                                            position="absolute"
                                            left={`${SPINE_X}px`}
                                            top={0}
                                            bottom={isLast ? undefined : 0}
                                            height={isLast ? `${NODE_CENTER_Y}px` : undefined}
                                            borderLeftWidth="1px"
                                            borderLeftStyle="solid"
                                            borderColor={lineColor}
                                        />
                                        {/* Horizontal connector from node to card, anchored to the
                                            node center so it lines up with the card's icon. */}
                                        <Box
                                            position="absolute"
                                            top={`${NODE_CENTER_Y}px`}
                                            left={`${SPINE_X + NODE_SIZE / 2}px`}
                                            right="6px"
                                            borderTopWidth="1px"
                                            borderTopStyle="solid"
                                            borderColor={lineColor}
                                        />
                                        {/* Node ring — outer Box centers it on the node line, inner
                                            Box runs the pulse so the scale animation doesn't fight the
                                            translateY used for centering. */}
                                        <Box
                                            position="absolute"
                                            top={`${NODE_CENTER_Y}px`}
                                            left={`${SPINE_X - NODE_SIZE / 2}px`}
                                            transform="translateY(-50%)"
                                            w={`${NODE_SIZE}px`}
                                            h={`${NODE_SIZE}px`}
                                            zIndex={1}
                                        >
                                            <Box
                                                w="100%"
                                                h="100%"
                                                borderRadius="full"
                                                bg="white"
                                                borderWidth="2px"
                                                borderColor={lineColor}
                                                color={lineColor}
                                                _groupHover={{ animation: `${nodePulse} 2.2s ease-in-out infinite` }}
                                            />
                                        </Box>
                                    </Box>

                                    {/* Card */}
                                    <Box flex={1} minW={0} pb={isLast ? 0 : 2}>
                                        {item.content}
                                    </Box>
                                </Flex>
                            );
                        })}
                    </Box>
                </Box>
            ))}
        </Box>
    </Box>
);

export default Timeline;
